// frontend/src/pages/HelpCentre.jsx
import { useState, useMemo } from 'react';
import { helpCategories, getAllArticles } from '../data/helpContent';

// ─── Simple Markdown Renderer ─────────────────────────────────────────────────
function renderMarkdown(md) {
  if (!md) return '';
  let html = md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^\|(.+)\|$/gm, (match, row) => {
      const cells = row.split('|').map(c => c.trim());
      const isHeader = cells.every(c => c === '' || /^-+$/.test(c));
      if (isHeader) return '';
      return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
    })
    .replace(/(<tr>[\s\S]+?<\/tr>)/g, (match) => {
      const rows = match.match(/<tr>[\s\S]*?<\/tr>/g) || [];
      if (!rows.length) return match;
      const header = '<tr>' + rows[0].replace(/<td>/g, '<th>').replace(/<\/td>/g, '</th>').replace('<tr>', '').replace('</tr>', '') + '</tr>';
      const body = rows.slice(1).join('');
      return `<table><thead>${header}</thead><tbody>${body}</tbody></table>`;
    })
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hultpc])/gm, '');
  return `<p>${html}</p>`;
}

// ─── Main Help Centre Component ───────────────────────────────────────────────
export default function HelpCentre() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticleId, setSelectedArticleId] = useState('overview');
  const [expandedCategories, setExpandedCategories] = useState(
    helpCategories.reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {})
  );

  const allArticles = useMemo(() => getAllArticles(), []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return allArticles.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.content.toLowerCase().includes(q) ||
      a.categoryLabel.toLowerCase().includes(q)
    );
  }, [searchQuery, allArticles]);

  const selectedArticle = useMemo(() =>
    allArticles.find(a => a.id === selectedArticleId),
    [selectedArticleId, allArticles]
  );

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const selectArticle = (id) => {
    setSelectedArticleId(id);
    setSearchQuery('');
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Help Centre</h1>
          <p style={styles.pageSubtitle}>Everything you need to use DifferentialPay. Use the AI Assistant for questions.</p>
        </div>
      </div>

      <div style={styles.layout}>
        {/* ── Left Sidebar ── */}
        <div style={styles.sidebar}>
          <div style={styles.searchWrapper}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              style={styles.searchInput}
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button style={styles.clearSearch} onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          {searchResults ? (
            <div style={styles.searchResults}>
              <p style={styles.searchResultsLabel}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
              </p>
              {searchResults.length === 0 ? (
                <p style={styles.noResults}>No articles found. Try different keywords.</p>
              ) : (
                searchResults.map(article => (
                  <button
                    key={article.id}
                    style={styles.searchResultItem}
                    onClick={() => selectArticle(article.id)}
                  >
                    <span style={styles.searchResultCategory}>
                      {article.categoryIcon} {article.categoryLabel}
                    </span>
                    <span style={styles.searchResultTitle}>{article.title}</span>
                  </button>
                ))
              )}
            </div>
          ) : (
            <nav style={styles.toc}>
              {helpCategories.map(cat => (
                <div key={cat.id} style={styles.tocCategory}>
                  <button
                    style={styles.tocCategoryHeader}
                    onClick={() => toggleCategory(cat.id)}
                  >
                    <span>{cat.icon} {cat.label}</span>
                    <span style={styles.tocChevron}>
                      {expandedCategories[cat.id] ? '▾' : '▸'}
                    </span>
                  </button>
                  {expandedCategories[cat.id] && (
                    <div style={styles.tocArticles}>
                      {cat.articles.map(article => (
                        <button
                          key={article.id}
                          style={{
                            ...styles.tocArticle,
                            ...(selectedArticleId === article.id ? styles.tocArticleActive : {})
                          }}
                          onClick={() => selectArticle(article.id)}
                        >
                          {article.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}
        </div>

        {/* ── Main Content ── */}
        <div style={styles.mainContent}>
          {selectedArticle ? (
            <>
              <div style={styles.articleHeader}>
                <span style={styles.articleBreadcrumb}>
                  {allArticles.find(a => a.id === selectedArticleId)?.categoryIcon}{' '}
                  {allArticles.find(a => a.id === selectedArticleId)?.categoryLabel}
                </span>
                <h2 style={styles.articleTitle}>{selectedArticle.title}</h2>
                {selectedArticle.roles && (
                  <div style={styles.roleBadges}>
                    {selectedArticle.roles.map(role => (
                      <span key={role} style={styles.roleBadge}>{role}</span>
                    ))}
                  </div>
                )}
              </div>
              <div
                style={styles.articleBody}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedArticle.content) }}
              />
            </>
          ) : (
            <p style={styles.noArticle}>Select an article from the left to get started.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const NAVY = '#1a2b4a';
const ACCENT = '#3b82f6';
const BORDER = '#e2e8f0';
const TEXT = '#1e293b';
const MUTED = '#64748b';
const BG = '#f8fafc';

const styles = {
  page: {
    minHeight: '100vh',
    width: '100%',
    background: BG,
    fontFamily: "'Segoe UI', sans-serif",
    color: TEXT,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  pageHeader: {
    background: NAVY,
    padding: '28px 32px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  pageTitle: { margin: 0, color: '#fff', fontSize: 24, fontWeight: 700 },
  pageSubtitle: { margin: '4px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 14 },
  layout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    width: '100%',
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    background: '#fff',
    borderRight: `1px solid ${BORDER}`,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  searchWrapper: {
    padding: '16px 16px 12px',
    borderBottom: `1px solid ${BORDER}`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  searchIcon: { fontSize: 14, flexShrink: 0 },
  searchInput: {
    flex: 1,
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    padding: '7px 10px',
    fontSize: 13,
    outline: 'none',
    background: BG,
    color: TEXT,
  },
  clearSearch: {
    background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 12,
  },
  searchResults: { padding: '12px', overflowY: 'auto' },
  searchResultsLabel: { fontSize: 12, color: MUTED, margin: '0 0 10px' },
  noResults: { fontSize: 13, color: MUTED, margin: 0 },
  searchResultItem: {
    display: 'flex', flexDirection: 'column', gap: 2, width: '100%',
    textAlign: 'left', background: 'none', border: `1px solid ${BORDER}`,
    borderRadius: 6, padding: '8px 10px', marginBottom: 6, cursor: 'pointer',
  },
  searchResultCategory: { fontSize: 11, color: MUTED },
  searchResultTitle: { fontSize: 13, color: TEXT, fontWeight: 500 },
  toc: { padding: '8px 0', overflowY: 'auto', flex: 1 },
  tocCategory: { marginBottom: 2 },
  tocCategoryHeader: {
    width: '100%', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '8px 16px', background: 'none',
    border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    color: TEXT, textAlign: 'left',
  },
  tocChevron: { color: MUTED, fontSize: 12 },
  tocArticles: { paddingLeft: 8 },
  tocArticle: {
    display: 'block', width: '100%', textAlign: 'left', background: 'none',
    border: 'none', padding: '6px 16px 6px 24px', fontSize: 13, color: MUTED,
    cursor: 'pointer', borderRadius: 4, transition: 'all 0.15s',
  },
  tocArticleActive: { color: ACCENT, background: '#eff6ff', fontWeight: 500 },
  mainContent: {
    flex: 1, overflowY: 'auto', padding: '32px 40px', minWidth: 0,
  },
  articleHeader: {
    marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${BORDER}`,
  },
  articleBreadcrumb: {
    fontSize: 12, color: MUTED, textTransform: 'uppercase',
    letterSpacing: '0.05em', display: 'block', marginBottom: 8,
  },
  articleTitle: { margin: '0 0 12px', fontSize: 26, fontWeight: 700, color: NAVY },
  roleBadges: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  roleBadge: {
    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
    background: '#eff6ff', color: ACCENT, border: `1px solid #bfdbfe`,
  },
  articleBody: { lineHeight: 1.75, fontSize: 15, color: TEXT },
  noArticle: { color: MUTED, fontSize: 15 },
};